import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "./Logo";

const regulationStates = [
  { name: 'Florida', path: '/regulations/florida' },
  { name: 'Georgia', path: '/regulations/georgia' },
  { name: 'South Carolina', path: '/regulations/south-carolina' },
  { name: 'North Carolina', path: '/regulations/north-carolina' },
];

const servicesLinks = [
  { name: 'Our Services', path: '/services' },
  { name: 'Compliance Management', path: '/compliance-services' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [regulationsOpen, setRegulationsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileRegulationsOpen, setMobileRegulationsOpen] = useState(false);
  const servicesDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isServicesActive = location.pathname === '/services' || location.pathname === '/compliance-services';
  const isRegulationsActive = location.pathname.startsWith('/regulations');

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setRegulationsOpen(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setServicesOpen(false);
    setRegulationsOpen(false);
    setMobileServicesOpen(false);
    setMobileRegulationsOpen(false);
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <header className="bg-white sticky top-0 z-50 shadow-soft transition-all duration-300">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center transition-colors hover:opacity-80">
            <Logo className="h-10 w-10 mr-2" />
            <div className="flex items-baseline">
              <span className="text-2xl md:text-3xl font-extrabold tracking-tight text-primary">APEX</span>
              <span className="text-2xl md:text-3xl font-extrabold tracking-tight text-accent ml-1">DSC</span>
            </div>
          </Link>

          {/* Mobile menu button */}
          <button
            className="md:hidden focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <NavLink
              to="/"
              className={({isActive}) =>
                isActive
                  ? "text-primary font-medium border-b-2 border-primary pb-1 transition-all"
                  : "text-gray-600 hover:text-primary border-b-2 border-transparent hover:border-primary-light pb-1 transition-all"
              }
              end
            >
              Home
            </NavLink>
            {/* Services Dropdown */}
            <div className="relative" ref={servicesDropdownRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className={`flex items-center pb-1 border-b-2 transition-all ${
                  isServicesActive
                    ? "text-primary font-medium border-primary"
                    : "text-gray-600 hover:text-primary border-transparent hover:border-primary-light"
                }`}
              >
                Services
                <svg className={`w-4 h-4 ml-1 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 animate-fade-in z-50">
                  {servicesLinks.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        location.pathname === item.path
                          ? 'text-primary font-medium bg-primary/5'
                          : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Regulations Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setRegulationsOpen(!regulationsOpen)}
                className={`flex items-center pb-1 border-b-2 transition-all ${
                  isRegulationsActive
                    ? "text-primary font-medium border-primary"
                    : "text-gray-600 hover:text-primary border-transparent hover:border-primary-light"
                }`}
              >
                Regulations
                <svg className={`w-4 h-4 ml-1 transition-transform ${regulationsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {regulationsOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 animate-fade-in z-50">
                  {regulationStates.map((state) => (
                    <Link
                      key={state.path}
                      to={state.path}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        location.pathname === state.path
                          ? 'text-primary font-medium bg-primary/5'
                          : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                      }`}
                    >
                      {state.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/about"
              className={({isActive}) =>
                isActive
                  ? "text-primary font-medium border-b-2 border-primary pb-1 transition-all"
                  : "text-gray-600 hover:text-primary border-b-2 border-transparent hover:border-primary-light pb-1 transition-all"
              }
            >
              About
            </NavLink>
            <NavLink
              to="/contact"
              className={({isActive}) =>
                isActive
                  ? "text-primary font-medium border-b-2 border-primary pb-1 transition-all"
                  : "text-gray-600 hover:text-primary border-b-2 border-transparent hover:border-primary-light pb-1 transition-all"
              }
            >
              Contact
            </NavLink>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link
              to="/get-quote"
              className="btn btn-primary px-5 py-2 rounded-lg font-medium bg-primary text-white hover:bg-primary-dark transition-all shadow-sm hover:shadow-md"
            >
              Get a Quote
            </Link>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pt-4 pb-3 border-t mt-3 animate-fade-in">
            <nav className="flex flex-col space-y-4">
              <NavLink
                to="/"
                className={({isActive}) =>
                  isActive
                    ? "text-primary font-medium px-2 py-1"
                    : "text-gray-600 hover:text-primary px-2 py-1"
                }
                end
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </NavLink>
              {/* Mobile Services Accordion */}
              <div>
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className={`flex items-center justify-between w-full px-2 py-1 ${
                    isServicesActive ? 'text-primary font-medium' : 'text-gray-600 hover:text-primary'
                  }`}
                >
                  Services
                  <svg className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileServicesOpen && (
                  <div className="ml-4 mt-2 space-y-2 animate-fade-in">
                    {servicesLinks.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-2 py-1 text-sm ${
                          location.pathname === item.path
                            ? 'text-primary font-medium'
                            : 'text-gray-500 hover:text-primary'
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Regulations Accordion */}
              <div>
                <button
                  onClick={() => setMobileRegulationsOpen(!mobileRegulationsOpen)}
                  className={`flex items-center justify-between w-full px-2 py-1 ${
                    isRegulationsActive ? 'text-primary font-medium' : 'text-gray-600 hover:text-primary'
                  }`}
                >
                  Regulations
                  <svg className={`w-4 h-4 transition-transform ${mobileRegulationsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileRegulationsOpen && (
                  <div className="ml-4 mt-2 space-y-2 animate-fade-in">
                    {regulationStates.map((state) => (
                      <Link
                        key={state.path}
                        to={state.path}
                        className={`block px-2 py-1 text-sm ${
                          location.pathname === state.path
                            ? 'text-primary font-medium'
                            : 'text-gray-500 hover:text-primary'
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {state.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <NavLink
                to="/about"
                className={({isActive}) =>
                  isActive
                    ? "text-primary font-medium px-2 py-1"
                    : "text-gray-600 hover:text-primary px-2 py-1"
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </NavLink>
              <NavLink
                to="/contact"
                className={({isActive}) =>
                  isActive
                    ? "text-primary font-medium px-2 py-1"
                    : "text-gray-600 hover:text-primary px-2 py-1"
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </NavLink>
              <Link
                to="/get-quote"
                className="btn btn-primary w-full text-center py-2 mt-2 rounded-lg font-medium bg-primary text-white hover:bg-primary-dark"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get a Quote
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
